import { CoordinateType, type Fn } from 'common/types.ts';
import { appendPersianOrderToString } from 'common/utils/numbers.ts';
import { TFulfillmentPackageType, TRendezvous } from 'src/common/api-calls/delivery-request/deliveryRequest.ts';
import type { NeoMapBoundsFitterProps, NeoMapMarkerProps } from '@neo-map/components';
import { TCourier } from 'common/api-calls/common-api-types.ts';
import { getPathToIcon } from 'src/common/utils/utilities';
import styles from './styles.module.scss';
import { TSavedContact } from 'src/features/select-location/types';
import {
  areCoordinatesTheSame,
  convertCoordinatesToLatLng,
  convertLatLngToCoordinateArray,
  convertLatLngToCoordinates,
  getCoordinatesDistance,
  isValidCoordinate,
} from './coordinateUtils';

export {
  areCoordinatesTheSame,
  convertCoordinatesToLatLng,
  convertLatLngToCoordinateArray,
  convertLatLngToCoordinates,
  getCoordinatesDistance,
  isValidCoordinate,
};

// todo: apply necessary changes on courier marker so that it sticks on the map
export const getCourierMarker = (location: CoordinateType, isCar?: boolean, zoomOnClick?: boolean): NeoMapMarkerProps => ({
  coordinates: location,
  imgSrc: getPathToIcon(isCar ? 'map-car' : 'map-motor'),
  width: 40,
  height: 40,
  anchor: 'bottom',
  bearing: location.bearing,
  zoomOnClick,
});

export const getFavoriteMarker = (location: CoordinateType, onClick: Fn): NeoMapMarkerProps => ({
  coordinates: location,
  imgSrc: getPathToIcon('map-fav'),
  width: 24,
  height: 24,
  anchor: 'center',
  bearing: location.bearing,
  zoomOnClick: true,
  onClick,
});

const noop = () => {};
export const getOriginMarker = (
  originLocation: CoordinateType,
  zoomOnClick?: boolean,
  clickAction: Fn = noop,
): NeoMapMarkerProps => ({
  coordinates: originLocation,
  imgSrc: getPathToIcon('origin-dot'),
  label: 'مبدأ',
  width: 20,
  height: 20,
  onClick: clickAction,
  zoomOnClick,
  labelClassName: styles.label,
  className: 'origin-marker',
});

export const getDestinationMarker = (
  destinationLocation: CoordinateType,
  order?: number,
  zoomOnClick?: boolean,
): NeoMapMarkerProps => ({
  coordinates: destinationLocation,
  imgSrc: getPathToIcon('destination-dot'),
  label: appendPersianOrderToString(order ?? 0, 'مقصد', 1),
  width: 20,
  height: 20,
  zoomOnClick,
  labelClassName: styles.label,
  className: `destination-marker-${order}`,
});

type Points = {
  origin: CoordinateType | undefined;
  destinations: (CoordinateType | undefined)[];
};

type GetMarkersOptions = {
  highlightedPartyIndex?: number;
};

const withScaledMarker = (marker: NeoMapMarkerProps, shouldScale: boolean): NeoMapMarkerProps =>
  shouldScale ? { ...marker, scaled: true } : marker;

export const getMarkersFromPoints = (points: Points, zoomOnClick?: boolean, options?: GetMarkersOptions): NeoMapMarkerProps[] => {
  const { highlightedPartyIndex } = options ?? {};
  const markers = [];
  if (points?.origin?.latitude && points?.origin?.longitude) {
    const originMarker = withScaledMarker(getOriginMarker(points.origin, zoomOnClick), highlightedPartyIndex === 0);
    if (originMarker.label && isValidCoordinate(originMarker.coordinates)) markers.push(originMarker);
  }
  if (points?.destinations?.length) {
    const length = points?.destinations?.length ?? 0;
    points.destinations.forEach((destination, idx) => {
      if (destination?.longitude && destination?.latitude) {
        const destinationMarker = withScaledMarker(
          getDestinationMarker(destination, length > 1 ? idx + 1 : 0, zoomOnClick),
          highlightedPartyIndex === idx + 1,
        );
        markers.push(destinationMarker);
      }
    });
  }
  return markers;
};

type DeliveryRequestLocations = {
  courier?: Pick<TCourier, 'coordinate'>;
  tripInformation: {
    origin: Pick<TRendezvous, 'location'>;
    destinations: Pick<TRendezvous, 'location'>[];
  };
  fulfillmentPackage: { type: TFulfillmentPackageType };
};

export const getMapMarkersFromDeliveryRequest = (
  deliveryRequest: DeliveryRequestLocations,
  zoomOnClick?: boolean,
  options?: GetMarkersOptions,
): NeoMapMarkerProps[] => {
  const courierCoordinate = deliveryRequest.courier?.coordinate;

  const locationMarkers = getMarkersFromPoints(
    {
      origin: deliveryRequest.tripInformation?.origin?.location.coordinate,
      destinations: (deliveryRequest.tripInformation?.destinations || []).map((item) => item?.location?.coordinate),
    },
    zoomOnClick,
    options,
  );
  const courierMarker =
    courierCoordinate && getCourierMarker(courierCoordinate, deliveryRequest?.fulfillmentPackage?.type === 'CAR_ON_DEMAND', true);

  return courierMarker ? [...locationMarkers, courierMarker] : locationMarkers;
};

export const getMapBoundsPropsFromDeliveryRequest = (
  locations: DeliveryRequestLocations,
  boundsOptions?: Omit<NeoMapBoundsFitterProps, 'coordinates'>,
): NeoMapBoundsFitterProps | undefined => {
  const tripInformationPoints = [
    locations.tripInformation?.origin?.location.coordinate,
    ...(locations?.tripInformation?.destinations || []).map((d) => d?.location?.coordinate),
  ];
  const points = locations.courier?.coordinate ? [locations.courier.coordinate, ...tripInformationPoints] : tripInformationPoints;
  const coordinates = points.filter((coordinate) => coordinate?.longitude && coordinate?.latitude);
  return {
    coordinates,
    ...boundsOptions,
  };
};

export const getMapMarkersFromContacts = (contacts: TSavedContact[], onClick: Fn<[TSavedContact]>): NeoMapMarkerProps[] => {
  return contacts.map((contact) => getFavoriteMarker(contact.coordinate, () => onClick(contact)));
};
